import React, { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Dialog from '@radix-ui/react-dialog';
import { useCurrentList } from '../context/CurrentListContext';
import { listAPI } from '../services/api';
import { Button } from './common/Button';
import { Input } from './common/Input';
import { EmojiPicker } from './common/EmojiPicker';
import { TrashIcon } from './common/icons';

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
);

const FamilyListBar: React.FC = () => {
  const {
    families, currentFamily, currentFamilyId, setCurrentFamilyId,
    currentListId, setCurrentListId, listsForCurrentFamily, currentList, reload,
  } = useCurrentList();

  const [createOpen, setCreateOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('🛒');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentFamilyId) return;
    setBusy(true); setError(null);
    try {
      const res = await listAPI.create({ name, emoji, family: currentFamilyId });
      await reload();
      if (res.data?._id) setCurrentListId(res.data._id);
      setCreateOpen(false); setName(''); setEmoji('🛒');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la création');
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!currentList || currentList.isMain) return;
    setBusy(true);
    try {
      await listAPI.delete(currentList._id);
      await reload();
      setConfirmOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    } finally { setBusy(false); }
  };

  // Main list first, then temp lists by creation order.
  const ordered = [...listsForCurrentFamily].sort((a, b) => Number(b.isMain) - Number(a.isMain));

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Family switcher */}
      {families.length > 1 ? (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button type="button" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 text-primary font-semibold text-sm hover:bg-primary/15 transition-colors focus:outline-none">
              <span>👨‍👩‍👧 {currentFamily?.name || 'Famille'}</span>
              <ChevronDown />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content sideOffset={6} className="z-[1000] min-w-[180px] bg-white rounded-xl shadow-strong border border-gray-100 p-1.5">
              <DropdownMenu.Label className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">Familles</DropdownMenu.Label>
              {families.map((f) => (
                <DropdownMenu.Item key={f._id} onSelect={() => setCurrentFamilyId(f._id)}
                  className={`px-3 py-2 rounded-lg text-sm cursor-pointer outline-none data-[highlighted]:bg-gray-100 ${f._id === currentFamilyId ? 'font-semibold text-primary' : 'text-gray-700'}`}>
                  {f.name}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      ) : (
        <span className="px-3 py-2 rounded-lg bg-primary/10 text-primary font-semibold text-sm">
          👨‍👩‍👧 {currentFamily?.name || 'Famille'}
        </span>
      )}

      <span className="text-gray-300">/</span>

      {/* List tabs */}
      <div className="flex flex-wrap items-center gap-1.5">
        {ordered.map((l) => (
          <button key={l._id} type="button" onClick={() => setCurrentListId(l._id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              l._id === currentListId ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
            }`}>
            <span>{l.emoji}</span>
            <span>{l.name}</span>
            {l.isMain && <span className={`text-[10px] uppercase tracking-wide ${l._id === currentListId ? 'text-white/70' : 'text-gray-400'}`}>principale</span>}
          </button>
        ))}

        {/* Add temp list */}
        <button type="button" onClick={() => { setError(null); setCreateOpen(true); }}
          className="px-2.5 py-2 rounded-lg text-sm font-medium bg-white text-gray-500 border border-dashed border-gray-300 hover:border-primary hover:text-primary transition-colors">
          + Liste
        </button>

        {/* Delete active temp list */}
        {currentList && !currentList.isMain && (
          <button type="button" onClick={() => { setError(null); setConfirmOpen(true); }}
            aria-label="Supprimer la liste" title="Supprimer cette liste"
            className="p-2 rounded-lg text-gray-400 hover:text-danger hover:bg-danger/10 transition-colors">
            <TrashIcon />
          </button>
        )}
      </div>

      {/* Create dialog */}
      <Dialog.Root open={createOpen} onOpenChange={setCreateOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 z-[1000]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[1001] w-[90vw] max-w-sm bg-white rounded-2xl shadow-strong p-6 focus:outline-none">
            <Dialog.Title className="text-lg font-bold text-gray-900 mb-1">Nouvelle liste</Dialog.Title>
            <Dialog.Description className="text-sm text-gray-500 mb-4">
              Pré-remplie avec les articles de la liste principale, tous à acheter.
            </Dialog.Description>
            <form onSubmit={handleCreate} className="flex flex-col gap-3">
              <Input label="Nom" value={name} onChange={(e) => setName(e.target.value)} placeholder="Soirée Pizza" autoFocus required fullWidth />
              <EmojiPicker value={emoji} onChange={setEmoji} disabled={busy} />
              {error && <p className="text-danger text-sm">{error}</p>}
              <div className="flex justify-end gap-2 mt-2">
                <Dialog.Close asChild><Button type="button" variant="outline" size="sm">Annuler</Button></Dialog.Close>
                <Button type="submit" variant="primary" size="sm" loading={busy} disabled={!name}>Créer</Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Delete confirm dialog */}
      <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 z-[1000]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[1001] w-[90vw] max-w-sm bg-white rounded-2xl shadow-strong p-6 focus:outline-none">
            <Dialog.Title className="text-lg font-bold text-gray-900 mb-1">Supprimer la liste</Dialog.Title>
            <Dialog.Description className="text-sm text-gray-500 mb-4">
              « {currentList?.name} » et tous ses articles seront supprimés. La liste principale n'est pas affectée.
            </Dialog.Description>
            {error && <p className="text-danger text-sm mb-2">{error}</p>}
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild><Button type="button" variant="outline" size="sm">Annuler</Button></Dialog.Close>
              <Button type="button" variant="danger" size="sm" loading={busy} onClick={handleDelete}>Supprimer</Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

export default FamilyListBar;
